/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/types/custom-report-template.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2024 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import { useQuery } from '@apollo/client/react';
import Loading from '@components/core/loading';
import { FieldChangeGrid } from 'views/pages/admin/audit-log/change-grid';
import {
    getProcessedFieldChangeItems,
    getFieldChangeItems,
    removePropertiesFromObject,
} from 'views/pages/admin/audit-log/service';
import Constants, { ReportStatusNames } from '@utils/constants';
import { getOrganisation } from '@queries/generated/uam.generated';
import { GET_ORGANISATION } from '@queries/uam';
import {
    useGetPowerBIModuleTypes,
    useGetPowerBIReportFormatTypes,
    useGetPowerBIReportTemplates,
} from '@apis/bff-api/report-templates';

const propArray = [
    '_id',
    'CurrentUserId',
    'CreatedDate',
    'CreatedByName',
    'CreatedByUserId',
    'UpdatedDate',
    'UpdatedByUserId',
    'UpdatedByName',
    'PublishReport',
    'HasGeneratedReports',
    'Organization',
    'OrgKey',
    'RecordId',
    'PbiDatasetId',
    'PbiReportId',
    'OrganizationReportTemplates',
    'Status',
];

const CMArray = ['IncludeComparisonDataset'];
const RAMArray = ['IncludeInternalRisks', 'IncludeNotApplicableRisks', 'IncludeReviews'];

const propArrayOrgReport = [...propArray, 'InUse'];

const propArrayForInUseRec = [
    ...propArray,
    ...CMArray,
    ...RAMArray,
    'Description',
    'Format',
    'Module',
    'IncludeSignatures',
    'IncludeNameAndVersion',
    'OrganizationReportTemplateRecordId',
];

const CustomReportTemplateRecordChangeGrid = ({ oldRecord, newRecord, oldItem, newItem }: any) => {
    // The creation-time values are not being stored intentionally.
    // For example, the Power BI template is retrieved dynamically from the Power BI API.
    // Storing a static snapshot would make it difficult to track changes to the template over time.
    // This approach was reviewed and agreed upon by the Product team.
    const { data: moduleTypesData, isLoading: isLoadingModuleTypes } = useGetPowerBIModuleTypes();
    const { data: formatTypesData, isLoading: isLoadingFormatTypes } = useGetPowerBIReportFormatTypes();
    const { data: orgsData, loading: isLoadingOrg } = useQuery<getOrganisation>(GET_ORGANISATION);
    const { data: powerBIReportTemplatesData, isLoading: isLoadingPowerBIReportTemplates } =
        useGetPowerBIReportTemplates(newRecord?.OrgKey || oldRecord?.OrgKey || orgsData?.organisation?.organisationKey);
    const { data: systemPowerBiReportTemplatesData, isLoading: isLoadingSystemPowerBIReportTemplates } =
        useGetPowerBIReportTemplates(Constants.ObjectId.Empty);

    const powerBIReportTemplates = powerBIReportTemplatesData?.concat(systemPowerBiReportTemplatesData ?? []);

    const oldRecordPowerBiTemplateName = oldRecord?.PbiReportId
        ? powerBIReportTemplates?.find((rt) => rt.id === oldRecord.PbiReportId)?.name
        : null;
    const newRecordPowerBiTemplateName = newRecord?.PbiReportId
        ? powerBIReportTemplates?.find((rt) => rt.id === newRecord.PbiReportId)?.name
        : null;

    const isInUseAuditRecord =
        (oldRecord?.OrganizationReportTemplateRecordId ||
            newRecord?.OrganizationReportTemplateRecordId ||
            oldRecord?.InUse ||
            newRecord?.InUse) ??
        false;

    const ArrayItems = (rec: any) => {
        if (
            rec.Module === Constants.Modules.CM ||
            rec.Module === Constants.Modules.CM2 ||
            rec.Module === Constants.Modules.SM
        ) {
            return propArrayOrgReport.concat(...RAMArray);
        }
        if (rec.Module === Constants.Modules.RAM) {
            return propArrayOrgReport.concat(...CMArray);
        }
        if (!Object.prototype.hasOwnProperty.call(rec, 'IncludeComparisonDataset')) {
            return propArrayOrgReport.concat(...CMArray);
        }
        return propArrayOrgReport.concat(...RAMArray);
    };
    const refactoredOldRecord = {
        ...(oldRecordPowerBiTemplateName ? { PowerBITemplate: oldRecordPowerBiTemplateName } : {}),
        ...oldRecord,
        ...removePropertiesFromObject(oldRecord, isInUseAuditRecord ? propArrayForInUseRec : ArrayItems(newRecord)),
    };
    const refactoredNewRecord = {
        ...(newRecordPowerBiTemplateName ? { PowerBITemplate: newRecordPowerBiTemplateName } : {}),
        ...newRecord,
        ...removePropertiesFromObject(newRecord, isInUseAuditRecord ? propArrayForInUseRec : ArrayItems(newRecord)),
    };

    const enums: Record<string, any> = {
        Module: moduleTypesData?.reduce(
            (acc, curr) => {
                acc[curr.id] = curr.name.replace(' ', '_');
                return acc;
            },
            {} as Record<number, string>,
        ),
        Format: ['', ...(formatTypesData?.map((i) => i.name) ?? [])],
        Status: ['', ...Object.values(ReportStatusNames)],
    };

    const lookups: any = {
        Organization: (oldRec: any, newRec: any) => newRec?.Organization?.Name,
    };

    const items = getFieldChangeItems(refactoredOldRecord, refactoredNewRecord, null, (field: string) =>
        getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem,
            newItem,
            field,
            enums,
            lookups,
        }),
    );

    if (
        isLoadingModuleTypes ||
        isLoadingFormatTypes ||
        isLoadingPowerBIReportTemplates ||
        isLoadingSystemPowerBIReportTemplates
    ) {
        return <Loading />;
    }
    return <FieldChangeGrid items={items} />;
};

export { CustomReportTemplateRecordChangeGrid };
